import React, { useState, useCallback, useMemo } from 'react';
import StatsTable from '@/AdminComponent/Reactions/components/Stats/StatsTable';
import { fetchReactionStats } from '@/api/adminApis/adminApis';
import { Button } from 'react-bootstrap';
import { GROUP_BY_OPTIONS, REACTION_GROUP_BY } from '@/constants/reactionStats';
import ComparisonModal from '@/AdminComponent/Reactions/components/Compare/ComparisonModal';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Toast from '@/utils/toastManager';

const ReactionStats = () => {
	const [groupBy, setGroupBy] = useState('');
	const [statsData, setStatsData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const navigate = useNavigate();

	const fetchStats = useCallback(async () => {
		if (!groupBy) {
			Toast.warn('Please select a grouping option.');
			return;
		}

		setLoading(true);
		setError(null);
		setStatsData(null);

		try {
			const { data } = await fetchReactionStats({ groupBy });
			setStatsData(data);
		} catch {
			setError('Failed to fetch data. Please try again.');
		} finally {
			setLoading(false);
		}
	}, [groupBy]);

	const groupByLabel = useMemo(
		() => GROUP_BY_OPTIONS.find(option => option.value === groupBy)?.label || 'Unknown',
		[groupBy]
	);

	return (
		<div className="reaction-stats-container">
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
				<Button variant="secondary" onClick={() => navigate(-1)}>
					<ArrowBackIosIcon />
				</Button>
				<h1>Reaction Statistics</h1>
			</div>

			<div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.63rem' }}>
				<InputLabel id="groupBySelect">Group By:</InputLabel>
				<Select
					labelId="groupBySelect"
					id="groupBySelectId"
					value={groupBy}
					onChange={e => setGroupBy(e.target.value)}
					style={{ minWidth: '10rem', height: '3rem' }}
					disabled={loading}
				>
					<MenuItem value="" disabled>
						Select Option
					</MenuItem>
					<MenuItem value={REACTION_GROUP_BY.CATEGORY}>Category</MenuItem>
					<MenuItem value={REACTION_GROUP_BY.INSTRUCTOR}>Instructor</MenuItem>
				</Select>
				<Button variant="info" onClick={fetchStats} disabled={!groupBy || loading} style={{ marginLeft: '0.63rem' }}>
					{loading ? 'Loading...' : 'Get Stats'}
				</Button>
				<Button
					variant="secondary"
					onClick={() => setIsModalOpen(true)}
					disabled={!groupBy || loading}
					style={{ marginLeft: '0.63rem' }}
				>
					Compare
				</Button>
			</div>

			{error && <p style={{ color: 'red' }}>Error: {error}</p>}
			{loading && <p>Loading data...</p>}

			{statsData && statsData.length > 0 && <StatsTable stats={statsData} groupBy={groupByLabel} />}
			{statsData && statsData.length === 0 && !loading && <p>No statistics found for the selected group.</p>}
			<ComparisonModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} groupBy={groupBy} />
		</div>
	);
};

export default ReactionStats;
